import React, {useMemo, useState} from 'react';
import Box from '@/Atoms/Box';
import {
  StyleSheet,
  Text,
  Image,
  ImageStyle,
  View,
  TouchableOpacity,
} from 'react-native';
import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';
import bookImg from '../../../Assets/Images/png/book_dummy.png';
import chapter from '../../../Assets/Images/png/eye.png';
import position from '../../../Assets/Images/png/shopping-bag.png';
import {FontSizes, vs} from '@/Utils/Dimensions';
import fontWeightToFamily from '@/Assets/Fonts/fontWeightToFamily';
import Spacer from '@/Atoms/Spacer';
import CCButton from '@/Atoms/CCButton';
import ProgressBar from '@/Atoms/ProgressBar';
import {useNavigation} from '@react-navigation/native';
import CCText from '@/Atoms/CCText';
import {formatDateToDDMMYYYY} from '@/Utils/CommonUtils';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';

function generateColor(index, total = 5) {
  // Calculate the hue based on the index and total number of items
  const hue = (360 / total) * index;
  // Convert HSL color to hexadecimal
  const hexColor = `hsl(${hue}, 70%, 50%)`;
  return hexColor;
}

const BookCard: React.FC<{
  id: number;
  book: string;
  index: number;
  progress: number;
  chapters: number;
  positions: number;
  type?: string;
  book_price?: number;
  book_mrp?: number;
  paid?: boolean;
  userData?: Object;
  userId?: number;
  orderBook?: ({}) => void;
  week?: string;
  completed?: boolean;
  chapter_details?: chapter[];
  create_timestamp?: string;
}> = ({
  id,
  book,
  index,
  progress,
  chapters,
  positions,
  type,
  book_price,
  book_mrp,
  paid,
  userData,
  userId,
  orderBook,
  week,
  completed,
  chapter_details,
  create_timestamp,
}) => {
  const navigation = useNavigation();
  const {isMobileOrTab} = useMediaQuery();
  const [isExpanded, setIsExpanded] = useState(false);
  const latestUnsolvedChapter = chapter_details?.find(ch => !ch.solved);
  const displayedChapters = isMobileOrTab
    ? [chapter_details]
    : [0, 1, 2].map(i =>
        chapter_details?.filter((_, index) => index % 3 === i),
      );

  const formattedDate = useMemo(() => {
    return formatDateToDDMMYYYY(create_timestamp);
  }, [create_timestamp]);

  return (
    <Box key={id} style={[styles.cardWrapper]}>
      <Box
        style={[
          styles.mainContainer,
          isMobileOrTab && {flexDirection: 'column', alignItems: 'flex-start'},
        ]}>
        <View style={{flexDirection: 'row', flex: 1}}>
          <View style={styles.weekContainer}>
            {week && (
              <View
                style={{
                  backgroundColor: CCColors?.Primary?.Brown,
                  borderRadius: 4,
                  width: '100%',
                  paddingVertical: 6,
                  alignItems: 'center',
                }}>
                <CCText
                  fontSize={FontSizes[12]}
                  style={{fontWeight: '600'}}
                  color={CCColors?.White}>
                  {week}
                </CCText>
              </View>
            )}
            <View style={{paddingVertical: 6}}>
              <CCText
                fontSize={FontSizes[10]}
                style={{fontWeight: '600'}}
                color={CCColors?.Primary?.Brown}>
                {formattedDate}
              </CCText>
            </View>
          </View>
          <View style={{marginLeft: 12, justifyContent: 'center'}}>
            <CCText
              fontSize={FontSizes[14]}
              color={CCColors.TextColor.Grey}
              numberOfLines={1}
              ellipsizeMode="tail"
              style={{
                lineHeight: 24,
                fontWeight: '500',
                maxWidth: '100%',
              }}>
              Name –{' '}
              <CCText color={CCColors?.TextColor?.Primary}>
                {book?.replace(/_/g, ' ')}
              </CCText>
            </CCText>
            <CCText
              fontSize={FontSizes[12]}
              numberOfLines={1}
              ellipsizeMode="tail"
              style={{
                fontWeight: '500',
                lineHeight: 24,
                maxWidth: '100%',
              }}>
              <CCText color={CCColors.TextColor.Grey}>
                Chapter{' '}
                {latestUnsolvedChapter?.chapter_index ??
                  chapter_details?.[0]?.chapter_index}{' '}
                –{' '}
              </CCText>
              <CCText color={CCColors?.TextColor?.Primary}>
                {latestUnsolvedChapter?.chapter_name ??
                  chapter_details?.[0]?.chapter_name}
              </CCText>
            </CCText>
          </View>
        </View>
        <View
          style={[
            {
              flexDirection: 'row',
              alignItems: 'center',
              marginTop: 10,
              gap: 15,
            },
            isMobileOrTab && {
              width: '100%',
              borderTopWidth: 1,
              borderTopColor: CCColors?.BorderBottom,
            },
          ]}>
          <View style={{flex: 1, width: isMobileOrTab ? '100%' : 'auto'}}>
            <ProgressBar
              style={{
                height: 6,
                backgroundColor: updateColorWithOpacity(
                  CCColors?.TextColor?.Dark,
                  0.1,
                ),
              }}
              progress={progress}
              fillStyle={{backgroundColor: CCColors?.Green}}
              text={undefined}
              icon={undefined}
            />
            <CCText numberOfLines={1} style={styles.progressText}>
              {(progress * 100).toFixed(0)}% complete
            </CCText>
          </View>
          <View
            style={[
              {
                flexDirection: 'row',
                alignItems: 'center',
                marginTop: isMobileOrTab ? 12 : 0,
              },
              isMobileOrTab && {alignSelf: 'flex-start'},
            ]}>
            <CCButton
              style={{
                backgroundColor:
                  progress > 0 && completed
                    ? CCColors.Primary.lightBrown
                    : CCColors.Primary.Default,
                borderRadius: 20,
                // paddingVertical: 6,
                // paddingHorizontal: 11,
                height: 40,
                justifyContent: 'center',
              }}
              onPress={() => {
                navigation.setParams({
                  name: book?.toLowerCase()?.replace(/\s/g, '-'),
                  bookKey: id,
                });
              }}
              titleStyle={{
                color: CCColors.TextColor.Dark,
                fontSize: FontSizes[14],
                lineHeight: 24,
                fontWeight: '500',
              }}>
              {progress === 1
                ? 'Start Again'
                : progress > 0
                ? 'Resume'
                : 'Start'}
            </CCButton>
            <TouchableOpacity
              style={{
                backgroundColor: CCColors?.Primary?.lightBrown,
                width: 32,
                height: 32,
                borderRadius: 20,
                justifyContent: 'center',
                alignItems: 'center',
                marginLeft: 10,
              }}
              onPress={() => setIsExpanded(!isExpanded)}>
              <Icon
                name={isExpanded ? 'chevron-up' : 'chevron-down'}
                size={24}
                color={CCColors.TextColor.Grey}
              />
            </TouchableOpacity>
          </View>
        </View>
      </Box>
      {isExpanded && (
        <Box style={styles.cardsRow} type="row-center-space-between">
          {displayedChapters.map((columnChapters, colIndex) => (
            <Box key={colIndex} style={styles.cardColumn}>
              {columnChapters?.map(chapter => (
                <Box
                  key={chapter.chapter_id}
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 10,
                    alignItems: 'center',
                    marginBottom: 8,
                  }}>
                  <Box
                    style={
                      chapter.solved
                        ? styles.iconCircleWrapper
                        : styles.iconCircleWrapperPending
                    }>
                    {chapter.solved ? (
                      <Icon name="check" size={16} color={CCColors?.Green} />
                    ) : (
                      <Icon
                        name="book-outline"
                        size={16}
                        color={CCColors.Red}
                      />
                    )}
                  </Box>
                  <CCText
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    style={[
                      styles.cardSubText,
                      chapter.solved && {
                        textDecorationLine: 'line-through',
                        color: CCColors?.TextColor?.Grey,
                      },
                    ]}>
                    {chapter.chapter_name}
                  </CCText>
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const styles = StyleSheet.create({
  cardWrapper: {
    backgroundColor: CCColors.White,
    borderRadius: 12,
    padding: 16,
    shadowColor: CCColors?.Black,
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
    width: '100%',
  },
  mainContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  weekContainer: {
    backgroundColor: CCColors?.White,
    borderWidth: 1,
    borderColor: CCColors?.Primary?.Brown,
    borderRadius: 8,
    padding: 6,
    width: 110,
    alignItems: 'center',
  },
  bold: {
    fontFamily: fontWeightToFamily('bold'),
  },
  progressText: {
    width: '100%',
    fontSize: FontSizes[14],
    fontFamily: fontWeightToFamily('regular'),
    fontWeight: '500',
    lineHeight: 16,
    color: CCColors.TextColor.Primary,
    marginTop: 4,
  },
  cardsRow: {
    marginTop: 20,
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingTop: 20,
    paddingHorizontal: 12,
    justifyContent: 'space-between',
    borderTopWidth: 1,
    borderTopColor: CCColors?.BorderBottom,
    gap: 12,
  },
  cardColumn: {
    flexBasis: '30%',
    flexGrow: 1,
    flexShrink: 1,
    marginBottom: 12,
  },
  iconCircleWrapper: {
    width: 32,
    height: 32,
    borderRadius: 20,
    backgroundColor: 'rgba(61, 171, 158, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  iconCircleWrapperPending: {
    width: 32,
    height: 32,
    borderRadius: 20,
    borderWidth: 0.1,
    borderColor: CCColors?.Box?.MiddleLine,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  cardSubText: {
    fontSize: FontSizes[10],
    color: CCColors.TextColor.Primary,
    marginBottom: 4,
    fontWeight: '500',
    lineHeight: 24,
    maxWidth: '80%',
  },
});

export default BookCard;
